import {
  createContext,
  useContext,
  useEffect,
  useId,
  useState,
  type ChangeEvent,
  type FormEvent,
  type ReactNode,
  type Ref,
  type RefObject,
} from "react";
import { Form, Link, useNavigation } from "react-router";
import type { AuthProviders } from "@repo/shared-types";
import { api } from "@/lib/api";
import { Check } from "lucide-react";
import { PageTitle } from "@/components/brand";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Field as UIField, FieldDescription, FieldError, FieldLabel, FieldLegend, FieldSet } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from "@/components/ui/input-group";
import { Separator } from "@/components/ui/separator";
import { Toggle } from "@/components/ui/toggle";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { cn } from "@/lib/utils";

type FieldProps = {
  label: string;
  name: string;
  type?: string;
  autoComplete?: string;
  required?: boolean;
  hint?: string;
  /** The api's message about this field, shown beside it rather than above the form. */
  error?: string;
  defaultValue?: string;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  autoFocus?: boolean;
  /** Said in the label, so nobody has to guess what may be left empty. */
  optional?: boolean;
};

/**
 * A labelled box. The browser checks what it can — required, length, pattern
 * — before anything is sent: every request to the auth routes spends one of
 * the few its IP has at once, and a typo should not.
 */
export function Field({
  label,
  name,
  type = "text",
  autoComplete,
  required = true,
  hint,
  error,
  defaultValue,
  minLength,
  maxLength,
  pattern,
  autoFocus,
  optional,
  trailing,
}: FieldProps & { trailing?: ReactNode }) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint && hintId, error && errorId].filter(Boolean).join(" ") || undefined;
  const box = {
    id,
    name,
    type,
    required,
    autoComplete,
    defaultValue,
    minLength,
    maxLength,
    pattern,
    autoFocus,
    "aria-invalid": error ? true : undefined,
    "aria-describedby": describedBy,
  };
  return (
    <UIField data-invalid={error ? true : undefined}>
      <FieldLabel htmlFor={id}>
        {label}
        {optional && <span className="font-normal text-muted-foreground"> (optional)</span>}
      </FieldLabel>
      {trailing ? (
        <InputGroup>
          <InputGroupInput {...box} />
          <InputGroupAddon align="inline-end">{trailing}</InputGroupAddon>
        </InputGroup>
      ) : (
        <Input {...box} />
      )}
      {error && <FieldError id={errorId}>{error}</FieldError>}
      {hint && <FieldDescription id={hintId}>{hint}</FieldDescription>}
    </UIField>
  );
}

/**
 * A password box with Show/Hide, which is what makes a second "confirm" box
 * unnecessary: a typo can be seen rather than retyped.
 */
export function PasswordField(props: Omit<FieldProps, "type" | "minLength" | "maxLength"> & { checkLength?: boolean }) {
  const [shown, setShown] = useState(false);
  const { checkLength = true, ...field } = props;
  return (
    <Field
      {...field}
      type={shown ? "text" : "password"}
      // The api's bounds, checked here first. Not on a current password,
      // which is whatever it was when it was chosen.
      minLength={checkLength ? 8 : undefined}
      maxLength={checkLength ? 128 : undefined}
      trailing={
        <InputGroupButton
          type="button"
          onClick={() => setShown((value) => !value)}
          aria-pressed={shown}
          aria-label={shown ? `Hide ${props.label.toLowerCase()}` : `Show ${props.label.toLowerCase()}`}
        >
          {shown ? "Hide" : "Show"}
        </InputGroupButton>
      }
    />
  );
}

/**
 * One of a few choices, all in sight and picked with one tap: what a phone
 * app uses where a web form would use radios, which this page is a draft of.
 * shadcn's toggle group, one item on at a time, which a screen reader hears
 * as radios and the arrow keys move through; a hidden input beside it sends
 * the choice with the form, since the group itself sends nothing.
 */
/** A choice whose value is "" — "Not now" — stands for none, which a toggle group cannot hold. */
const NONE = "\u0000none";

export function SegmentedControl<T extends string>({
  label,
  name,
  options,
  value,
  onChange,
  shown,
  labelHidden = false,
  fill = false,
}: {
  /** What a screen reader hears it called, and what it shows unless `shown` says otherwise. */
  label: string;
  name: string;
  options: { value: T; label: string }[];
  /** "" for none chosen yet. */
  value: T | "";
  onChange: (value: T) => void;
  /** A shorter label to show, where the sheet around it already says the rest ("Level" in Part A's). */
  shown?: string;
  /** Heard and not shown, where the choices say it all (Account's theme). */
  labelHidden?: boolean;
  /** As wide as the page at every width, where the page is this one choice (Account's division). */
  fill?: boolean;
}) {
  return (
    <FieldSet className="gap-2">
      <FieldLegend variant="label" className={labelHidden ? "sr-only" : undefined}>
        {shown ?? label}
      </FieldLegend>
      <ToggleGroup
        type="single"
        variant="outline"
        spacing={0}
        value={value === "" ? NONE : value}
        // Tapping the one already chosen asks for none; a choice stays made.
        onValueChange={(chosen) => chosen && onChange((chosen === NONE ? "" : chosen) as T)}
        aria-label={label}
        className={fill ? "w-full" : "w-full sm:w-fit"}
      >
        {options.map((option) => (
          // Equal while every label fits; a longer one ("Placement points") takes its width, never spills over.
          <ToggleGroupItem key={option.value} value={option.value === "" ? NONE : option.value} className={cn("min-w-fit flex-1", !fill && "sm:flex-none")}>
            {option.label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
      <input type="hidden" name={name} value={value} />
    </FieldSet>
  );
}

/**
 * Several things, each on or off: chips that wrap, ticked when on, what a
 * phone app uses where a web form would use checkboxes. shadcn's toggles,
 * each pressed or not; a hidden input for each one on sends it with the form
 * as a checkbox would ("on"). Under them, a line for each one on that says
 * what it does — a filter's chips say nothing.
 */
export function ToggleChips({
  label,
  options,
}: {
  /** The group's name for a screen reader; nothing shows it. */
  label: string;
  options: { name: string; label: string; says?: string; on: boolean; onChange: (on: boolean) => void }[];
}) {
  return (
    <div className="space-y-2">
      <div role="group" aria-label={label} className="flex flex-wrap gap-2">
        {options.map((option) => (
          <Toggle key={option.name} variant="outline" pressed={option.on} onPressedChange={option.onChange}>
            {option.on && <Check aria-hidden="true" />}
            {option.label}
          </Toggle>
        ))}
      </div>
      {options
        .filter((option) => option.on)
        .map((option) => (
          <input key={option.name} type="hidden" name={option.name} value="on" />
        ))}
      {options.some((option) => option.on && option.says) && (
        <ul className="space-y-0.5 text-xs text-muted-foreground">
          {options
            .filter((option) => option.on && option.says)
            .map((option) => (
              <li key={option.name}>{option.says}</li>
            ))}
        </ul>
      )}
    </div>
  );
}

/**
 * The account a new password belongs to, for password managers: a form
 * choosing a password with no username box beside it is saved under nothing,
 * or under the handle. Hidden from people; read by the manager.
 */
export function PasswordManagerUsername({ email }: { email: string }) {
  return (
    <Input
      type="email"
      name="account-email"
      autoComplete="username"
      value={email}
      readOnly
      hidden
    />
  );
}

/**
 * Choosing a picture: the file box, out of sight, inside shadcn's label drawn
 * as an outlined button — tapping it opens the device's own picker, and it
 * names the box. Focus shows on it, and a disabled box greys it. One
 * component for every picker, so none drifts from the others.
 */
export function PictureButton({
  children,
  name,
  size = "default",
  disabled,
  inputRef,
  onChange,
}: {
  children: ReactNode;
  name?: string;
  size?: "sm" | "default" | "lg";
  disabled?: boolean;
  /** To empty the box when what was chosen is let go. */
  inputRef?: Ref<HTMLInputElement>;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
}) {
  return (
    <Label
      className={cn(
        buttonVariants({ variant: "outline", size }),
        // A label is set tight and unselectable; a button is neither.
        "relative cursor-pointer leading-normal has-[:disabled]:pointer-events-none has-[:disabled]:opacity-50 has-[:focus-visible]:border-ring has-[:focus-visible]:ring-3 has-[:focus-visible]:ring-ring/30",
      )}
    >
      {children}
      <Input ref={inputRef} type="file" name={name} accept="image/*" disabled={disabled} className="sr-only" onChange={onChange} />
    </Label>
  );
}

/**
 * The honeypot: a box nobody sees or reaches with Tab, which a script filling
 * every field fills. Off screen rather than display: none, which some
 * scripts know to skip. The api answers as it would a person, and sends
 * nothing.
 */
export function Honeypot() {
  return (
    <div aria-hidden="true" className="absolute -left-[10000px] h-px w-px overflow-hidden">
      <Label>
        Website
        <Input type="text" name="website" tabIndex={-1} autoComplete="off" defaultValue="" />
      </Label>
    </div>
  );
}

/** How long sending a link again waits: the api sends an address one a minute. */
export const RESEND_AFTER_SECONDS = 60;

/**
 * "Send it again", posting the same fields back to the page's action. It
 * waits `wait` seconds first, as the api does — sooner would send nothing.
 * Keyed by the caller on each answer, so every send starts the wait afresh.
 */
export function ResendForm({ fields, wait: from }: { fields: Record<string, string>; wait: number }) {
  const navigation = useNavigation();
  const [wait, setWait] = useState(from);
  useEffect(() => {
    if (wait <= 0) return;
    const timer = setTimeout(() => setWait((seconds) => seconds - 1), 1000);
    return () => clearTimeout(timer);
  }, [wait]);
  const sending = navigation.state === "submitting";

  return (
    <Form method="post">
      {Object.entries(fields).map(([name, value]) => (
        <input key={name} type="hidden" name={name} value={value} />
      ))}
      <Button type="submit" variant="outline" size="lg" disabled={wait > 0 || sending} className="w-full">
        {sending ? "Sending…" : wait > 0 ? `Send it again in ${wait}s` : "Send it again"}
      </Button>
    </Form>
  );
}

export function SubmitButton({ pending, children }: { pending: boolean; children: ReactNode }) {
  return (
    <Button type="submit" size="lg" disabled={pending} className="w-full">
      {children}
    </Button>
  );
}

/**
 * The one box a form refused, by its name, and why (decided with Tiago 20
 * October): the box is outlined and the message sits under it, where the
 * person is looking, rather than in a note at the form's foot. A reader of
 * this app's forms says which box with its `field`; a message that names
 * none is still the `ErrorNote`.
 */
export type FieldProblem = { field: string; error: string };

const Problem = createContext<FieldProblem | null>(null);

/** Hands a form's refused box down to whatever draws it. */
export const ProblemProvider = Problem.Provider;

/** Whether a box is the refused one, or one of a refused group's: "parts.0.timeLimit" covers its ".min". */
export function covers(field: string, name: string): boolean {
  return name === field || name.startsWith(`${field}.`);
}

/** The message for the box named `name`, while it is the one refused. */
export function useProblem(name: string): string | undefined {
  const problem = useContext(Problem);
  return problem && covers(problem.field, name) ? problem.error : undefined;
}

/** The message under a refused box, or nothing. */
export function ProblemNote({ name }: { name: string }) {
  const error = useProblem(name);
  return error ? <FieldError>{error}</FieldError> : null;
}

/**
 * What a form shows of a refusal: the action's or the reader's, as `sent` —
 * its `error` and `field` — kept until the box it names changes, and moved
 * to as it arrives. `onChange` goes on the form. A refusal naming no box is
 * `general`, for an `ErrorNote`.
 */
export function useFormProblem(
  sent: { error?: string; field?: string } | null | undefined,
  form: RefObject<HTMLFormElement | null>,
) {
  const named = (from: typeof sent): FieldProblem | null =>
    from?.error && from.field ? { field: from.field, error: from.error } : null;
  // Read afresh for each answer, never in an effect: a new answer is a new object.
  const [answer, setAnswer] = useState(sent);
  const [problem, setProblem] = useState(() => named(sent));
  if (sent !== answer) {
    setAnswer(sent);
    setProblem(named(sent));
  }
  useEffect(() => {
    if (problem && form.current) goTo(form.current, problem.field);
  }, [problem, form]);
  return {
    problem,
    setProblem,
    general: sent?.error && !sent.field ? sent.error : undefined,
    onChange: (event: FormEvent<HTMLFormElement>) => {
      const name = (event.target as { name?: string }).name;
      if (problem && name && covers(problem.field, name)) setProblem(null);
    },
  };
}

/** Focuses the refused box — or, for one with nothing to focus, brings its message into sight. */
function goTo(form: HTMLFormElement, field: string) {
  const box = Array.from(form.elements).find(
    (element): element is HTMLInputElement =>
      "name" in element && typeof element.name === "string" && covers(field, element.name) && (element as HTMLInputElement).type !== "hidden",
  );
  if (box) {
    box.focus();
    box.scrollIntoView?.({ block: "center" });
  } else {
    form.querySelector('[data-slot="field-error"]')?.scrollIntoView?.({ block: "center" });
  }
}

export function ErrorNote({ message }: { message: string }) {
  return (
    <Alert variant="destructive">
      <AlertDescription>{message}</AlertDescription>
    </Alert>
  );
}

export function AuthCard({ title, children, footer }: { title: string; children: ReactNode; footer: ReactNode }) {
  return (
    <section className="mx-auto max-w-sm space-y-6">
      <PageTitle of={[title]} />
      <h1 className="page-title">{title}</h1>
      <Card>
        <CardContent className="space-y-4">{children}</CardContent>
      </Card>
      <p className="text-center text-sm text-muted-foreground">{footer}</p>
    </section>
  );
}

/**
 * Social sign-in buttons, rendered only for providers the api reports as
 * configured — a button for an unconfigured provider would bounce straight
 * back with an error.
 *
 * These are plain links, not fetches: the flow leaves this origin and returns
 * through a redirect that sets the session cookie, which a fetch cannot do.
 */
export function ProviderButtons({ providers, next }: { providers: AuthProviders; next: string }) {
  if (!providers.google) return null;

  return (
    <>
      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <Separator className="flex-1" />
        or
        <Separator className="flex-1" />
      </div>

      <div className="space-y-2">
        {providers.google && (
          <a
            href={api.auth.oauthUrl("google", next)}
            className={buttonVariants({ variant: "outline", size: "lg", className: "w-full" })}
          >
            Continue with Google
          </a>
        )}
      </div>
    </>
  );
}

/**
 * The api redirects back to /sign-in with one of these when a provider round
 * trip fails, so the page can say what happened rather than showing nothing.
 */
export const OAUTH_ERRORS: Record<string, string> = {
  oauth_denied: "Sign-in was cancelled.",
  oauth_state: "That sign-in attempt expired. Please try again.",
  oauth_failed: "The provider could not be reached. Please try again.",
  email_unverified: "That provider has not verified your email address.",
  account_exists:
    "An account with this email already exists, but its address was never verified. Choose a password for it with “Forgot password?” — that proves the address — then continue with the provider.",
  unknown_provider: "That sign-in method is not available.",
  invalid_token:
    "That link is no longer valid. Sign in, or choose a new password with “Forgot password?”.",
  server_error: "Something went wrong on our side. Please try again.",
};

export function SignInLink() {
  return (
    <Link to="/sign-in" className="font-medium underline underline-offset-4">
      Sign in
    </Link>
  );
}
